/**
 * Tunnel cross-section in metres, for either section the app draws: the
 * D-shape (vertical walls, WS = W) and the horseshoe (battered walls, from W
 * at the invert out to WS at the SPL). The crown is the circular arc through
 * the SPL corners (±WS/2, HW) and the apex (0, H), exactly as the renderers
 * build it.
 *
 * Coordinates: d is the offset from the centreline, Hp the height above the
 * invert, both in metres.
 */

const toDegree = (radian) => radian * (180 / Math.PI);

/**
 * A wall as high as the tunnel leaves no crown arch: a box section with a
 * flat roof. The crown formulas divide by the crown rise H − HW, so a box is
 * computed with the rise shrunk to this many metres — the arch through the
 * SPL corners is then flat to well under a millimetre.
 */
export const BOX_CROWN_RISE = 0.001;

export const isBoxSection = (dim) =>
  Number(dim.height) > 0 && Number(dim.hw) >= Number(dim.height);

/** Wall height the crown formulas can use; differs from HW only for a box. */
export const effectiveWallHeight = (dim) =>
  isBoxSection(dim) ? Number(dim.height) - BOX_CROWN_RISE : Number(dim.hw);

export const BOX_SECTION_ALERT =
  "Wall Height equals Tunnel Height: the tunnel is box-shaped (flat roof, no crown arch).";

/** Metres-geometry for the cross-section solvers (shared/alphaZero). */
export function sectionGeometry(dim, { battered = false } = {}) {
  const W = Number(dim.width);
  const WS = battered ? Number(dim.widthspl) : W;
  const H = Number(dim.height);
  const HW = effectiveWallHeight(dim);
  const HC = H - HW;
  const R = ((WS / 2) ** 2 + HC ** 2) / (2 * HC);
  const Theta = toDegree(Math.atan((R - HC) / (WS / 2)));
  const Arc = ((Math.PI * R) / 2) * (1 - Theta / 90);
  return {
    W,
    WS,
    H,
    HW,
    HWincl: Math.hypot(HW, (WS - W) / 2),
    R,
    HWn: H - R,
    Theta,
    Arc,
    ArcFull: 2 * Arc,
  };
}

const TOLERANCE = 1e-9;

/**
 * Range of heights at offset d that lie on the tunnel face: from the invert
 * (or, beyond the invert width, the battered wall) up to the crown.
 * Returns null when d is wider than the section.
 */
export function faceHeightRange(d, geom) {
  const x = Math.abs(Number(d));
  const { W, WS, HW, R, HWn } = geom;
  if (!isFinite(x) || x > WS / 2 + TOLERANCE) return null;
  const max = HWn + Math.sqrt(Math.max(0, R * R - x * x));
  let min = 0;
  if (x > W / 2 + TOLERANCE) {
    const taper = HW > 0 ? (WS - W) / 2 / HW : 0;
    min = taper > 0 ? (x - W / 2) / taper : Infinity;
  }
  return min <= max + TOLERANCE ? { min, max } : null;
}
